import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Zap } from "lucide-react";
import Seo from "../components/Seo";
import api from "../lib/api";

export default function Ministries() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/groups")
      .then((r) => setGroups(r.data))
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main id="main-content" className="bg-[#050505] text-white" data-testid="ministries-page">
      <Seo
        title="Ministries"
        description="16 ministries at The Push Center — from worship to youth, outreach, and intercessory prayer. Find your place to serve."
        path="/ministries"
      />
      <section className="relative pt-20 pb-16 lg:pt-32 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/worship-team.jpg" alt="" className="w-full h-full object-cover object-top opacity-70 bg-zoom" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/20" />
          <div className="absolute inset-0 lightning-grid opacity-30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-5">
            Get Involved
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] max-w-4xl">
            Find your place. <span className="italic text-electric text-pulse">Serve with purpose.</span>
          </h1>
          <p className="mt-7 text-lg text-zinc-300 max-w-2xl leading-relaxed">
            You weren't designed to sit on the sidelines. Every ministry at The Push Center is a place
            to grow, serve, and walk in your gifts.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-24 pt-12">
        {loading ? (
          <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="ministry-grid">
            {groups.map((g, i) => (
              <motion.div
                key={g.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: (i % 8) * 0.05 }}
                className="group relative rounded-2xl border border-white/5 bg-[#0a0a0a] hover:border-electric/50 transition-all duration-300 overflow-hidden"
                data-testid={`ministry-card-${g.slug}`}
              >
                <Link
                  to={`/ministries/${g.slug}`}
                  className="relative block"
                  data-testid={`ministry-link-${g.slug}`}
                >
                  {/* IMAGE / FALLBACK */}
                  <div className="relative h-44 overflow-hidden bg-[#050505]">
                    {(g.thumb_url || g.image_url) ? (
                      <img
                        src={g.thumb_url || g.image_url}
                        alt={g.name}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-electric/15 via-[#0c0c0c] to-[#0a0a0a]">
                        <span className="text-5xl" aria-hidden="true">{g.icon || "✨"}</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/30 to-transparent" />
                    <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 flex items-center justify-center group-hover:bg-electric group-hover:border-electric transition-all">
                      <ArrowUpRight className="w-4 h-4 text-white group-hover:text-black group-hover:rotate-12 transition-all" />
                    </div>
                  </div>

                  {/* TEXT */}
                  <div className="p-5">
                    <div className="font-display font-bold text-lg mb-1.5 leading-tight group-hover:text-electric transition-colors">{g.name}</div>
                    <div className="text-sm text-zinc-400 leading-relaxed mb-3 line-clamp-2">{g.description}</div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-electric font-bold inline-flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Zap className="w-3 h-3" /> Join this team
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-5xl mx-auto px-5 lg:px-10 pb-32">
        <div className="relative p-10 lg:p-16 rounded-3xl bg-gradient-to-br from-electric/15 via-[#0c0c0c] to-[#0c0c0c] border border-electric/20 text-center">
          <div className="absolute inset-0 lightning-grid opacity-20 rounded-3xl" />
          <div className="relative">
            <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tighter mb-5">
              Ready to step in?
            </h2>
            <p className="text-zinc-300 mb-8 max-w-xl mx-auto">
              Reach out and we'll connect you with the ministry where you'll thrive.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-electric text-black px-7 py-4 rounded-full font-bold tracking-wide hover:bg-[#4ade80] hover:-translate-y-1 transition-all duration-300"
              data-testid="ministries-cta-join"
            >
              Connect With Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
